import { createSelector } from '@reduxjs/toolkit';
import { OperatorType } from 'vet-bones/utils/queryBuilder';

import { projectRulesByIdSelector } from 'src/selectors/projectRules';

type ClauseOptions = {
  includeIfClause: boolean;
  includeThenClause: boolean;
};

/**
 * @returns rules with a class as a domain
 */
export const projectRulesWithDomainSelector = (
  projectId: string,
  domainIri: string,
  options: ClauseOptions = {
    includeIfClause: true,
    includeThenClause: true,
  }
) =>
  createSelector(projectRulesByIdSelector(projectId), (rulesById) =>
    Object.fromEntries(
      Object.entries(rulesById).filter(([_, rule]) =>
        [
          ...(options.includeIfClause
            ? rule.ifClause.data.query.expressions
            : []),
          ...(options.includeThenClause
            ? rule.thenClause.data.query.expressions
            : []),
        ].some((subExpression) =>
          subExpression.expressions.some((expression) =>
            expression.filters.some(
              (filter) => filter.subject.iri === domainIri
            )
          )
        )
      )
    )
  );

/**
 * @returns rules with a class as a range for a relationship
 */
export const projectRulesWithRangeSelector = (
  projectId: string,
  rangeIri: string,
  options: ClauseOptions = {
    includeIfClause: true,
    includeThenClause: true,
  }
) =>
  createSelector(projectRulesByIdSelector(projectId), (rulesById) =>
    Object.fromEntries(
      Object.entries(rulesById).filter(([_, rule]) =>
        [
          ...(options.includeIfClause
            ? rule.ifClause.data.query.expressions
            : []),
          ...(options.includeThenClause
            ? rule.thenClause.data.query.expressions
            : []),
        ].some((subExpression) =>
          subExpression.expressions.some((expression) =>
            expression.filters.some(
              (filter) =>
                typeof filter.value === 'object' &&
                filter.value.iri === rangeIri
            )
          )
        )
      )
    )
  );

/**
 * @returns rules that use a class
 */
export const projectRulesWithClassSelector = (
  projectId: string,
  classIri: string,
  options: ClauseOptions = {
    includeIfClause: true,
    includeThenClause: true,
  }
) =>
  createSelector(
    projectRulesWithDomainSelector(projectId, classIri, options),
    projectRulesWithRangeSelector(projectId, classIri, options),
    (rulesForDomain, rulesForRange) => ({ ...rulesForDomain, ...rulesForRange })
  );

/**
 * @returns rules that use an attribute
 */
export const projectRulesWithAttributeSelector = (
  projectId: string,
  attributeIri: string,
  options: ClauseOptions = {
    includeIfClause: true,
    includeThenClause: true,
  }
) =>
  createSelector(projectRulesByIdSelector(projectId), (rulesById) =>
    Object.fromEntries(
      Object.entries(rulesById).filter(([_, rule]) =>
        [
          ...(options.includeIfClause
            ? rule.ifClause.data.query.expressions
            : []),
          ...(options.includeThenClause
            ? rule.thenClause.data.query.expressions
            : []),
        ].some((subExpression) =>
          subExpression.expressions.some((expression) =>
            expression.filters.some(
              (filter) =>
                filter.operator.operatorType === OperatorType.DATATYPE &&
                filter.operator.iri === attributeIri
            )
          )
        )
      )
    )
  );

/**
 * @returns rules that use a relationship
 */
export const projectRulesWithRelationshipSelector = (
  projectId: string,
  relationshipIri: string,
  options: ClauseOptions = {
    includeIfClause: true,
    includeThenClause: true,
  }
) =>
  createSelector(projectRulesByIdSelector(projectId), (rulesById) =>
    Object.fromEntries(
      Object.entries(rulesById).filter(([_, rule]) =>
        [
          ...(options.includeIfClause
            ? rule.ifClause.data.query.expressions
            : []),
          ...(options.includeThenClause
            ? rule.thenClause.data.query.expressions
            : []),
        ].some((subExpression) =>
          subExpression.expressions.some((expression) =>
            expression.filters.some(
              (filter) =>
                filter.operator.operatorType === OperatorType.OBJECT &&
                filter.operator.iri === relationshipIri
            )
          )
        )
      )
    )
  );

/**
 * @returns rules that use an attribute for a given class
 */
export const projectRulesWithClassAttributeSelector = (
  projectId: string,
  classIri: string,
  attributeIri: string,
  options: ClauseOptions = {
    includeIfClause: true,
    includeThenClause: true,
  }
) =>
  createSelector(
    projectRulesWithAttributeSelector(projectId, attributeIri, options),
    projectRulesWithDomainSelector(projectId, classIri, options),
    (attributes, classes) =>
      Object.fromEntries(
        Object.entries(attributes).filter(([ruleId]) => classes[ruleId])
      )
  );

/**
 * @returns rules that generate a provided relationship for a provided class
 */
export const projectRulesWithClassRelationshipSelector = (
  projectId: string,
  classIri: string,
  relationshipIri: string,
  options: ClauseOptions = {
    includeIfClause: true,
    includeThenClause: true,
  }
) =>
  createSelector(
    projectRulesWithRelationshipSelector(projectId, relationshipIri, options),
    projectRulesWithClassSelector(projectId, classIri, options),
    (relationships, classes) =>
      Object.fromEntries(
        Object.entries(relationships).filter(([ruleId]) => classes[ruleId])
      )
  );
